import { AxleMenu } from '@/components/AxleMenu';
import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import {
    VisitRequestActionsMenuQuery,
    VisitRequestActionsMenuQueryVariables,
} from '@/components/visitRequests/VisitRequestActionsMenu.gql.types';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import {
    REASSIGN_DISABLED_NO_SERVICES_TOOLTIP,
    useCanReassignVisitRequest,
    visitRequestHasServices,
} from '@/hooks/permissions/visitRequest';
import {
    BookVisitRequestLinkParamsFragment,
    buildBookVisitRequestLink,
} from '@/pages/VisitBooking/useBookVisitRequestLink';
import { isOrderDiscontinued } from '@/types/order';
import { useBooleanState } from '@/utils/booleanState';
import { gql, useQuery } from '@apollo/client';
import { MoreVert } from '@mui/icons-material';
import { VisitRequestState } from 'gql-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useCancelVisitRequest } from './mutations/cancelVisitRequest';
import { useUncancelVisitRequest } from './mutations/uncancelVisitRequest';

const VISIT_REQUEST_ACTIONS_MENU_QUERY = gql`
    query VisitRequestActionsMenu($id: UUID!) {
        visitRequest(id: $id) {
            id
            state
            workflowStep
            permissions {
                id
                edit
            }
            visit {
                id
            }
            appointments {
                id
                services {
                    id
                }
            }
            order {
                id
                discontinuedAt
            }
            ...BookVisitRequestLinkVisitRequestLink
        }
    }
    ${BookVisitRequestLinkParamsFragment}
`;

export const VisitRequestActionsMenu = ({ visitRequestId }: { visitRequestId: string }) => {
    // Hooks.
    const history = useHistory();

    // Modal state.
    const [isMenuOpen, openMenu, closeMenu] = useBooleanState(false);
    const [hasHoveredMenu, setHasHoveredMenu] = useState(false);
    const iconButtonRef = useRef<HTMLElement>();

    const onMouseEnter = useCallback(() => {
        setHasHoveredMenu(true);
    }, []);

    useEffect(() => {
        const { current: iconButton } = iconButtonRef;

        if (iconButton) {
            iconButton.addEventListener('mouseenter', onMouseEnter);

            return () => {
                iconButton.removeEventListener('mouseenter', onMouseEnter);
            };
        }
    }, [onMouseEnter]);

    // Basic info query.
    const { data } = useQuery<VisitRequestActionsMenuQuery, VisitRequestActionsMenuQueryVariables>(
        VISIT_REQUEST_ACTIONS_MENU_QUERY,
        {
            variables: { id: visitRequestId },
            // Skip only if both false in case mouse enter event never fires
            skip: !hasHoveredMenu && !isMenuOpen,
        },
    );
    // Mutations
    const { cancelVisitRequest, loading: cancelVisitRequestLoading } = useCancelVisitRequest({
        visitRequestId,
    });
    const { uncancelVisitRequest, loading: uncancelVisitRequestLoading } = useUncancelVisitRequest({
        visitRequestId,
    });

    const { openDialog } = useDialog();
    const canReassignVisitRequest = useCanReassignVisitRequest(data?.visitRequest);
    const hasServices = visitRequestHasServices(data?.visitRequest);

    const mutationLoading = cancelVisitRequestLoading || uncancelVisitRequestLoading;

    const visitRequest = data?.visitRequest;
    const hasDiscontinuedOrder = isOrderDiscontinued(visitRequest?.order?.discontinuedAt ?? null);
    const isEditable =
        visitRequest?.permissions.edit &&
        visitRequest.state === VisitRequestState.PENDING &&
        !hasDiscontinuedOrder;

    const onReassignVisitRequest = useCallback(() => {
        closeMenu();
        if (!data?.visitRequest) {
            return;
        }
        if (data.visitRequest.visit?.id) {
            openDialog(DialogType.ReassignVisits, {
                visitsToReassign: [data.visitRequest.visit.id],
                visitRequestsToReassign: [],
                cliniciansToReassign: [],
            });
        } else {
            openDialog(DialogType.ReassignVisits, {
                visitsToReassign: [],
                visitRequestsToReassign: [data.visitRequest.id],
                cliniciansToReassign: [],
            });
        }
    }, [closeMenu, data?.visitRequest, openDialog]);

    const onEditVisitRequest = useCallback(() => {
        closeMenu();
        openDialog(DialogType.EditVisitRequest, { visitRequestId });
    }, [closeMenu, openDialog, visitRequestId]);

    return (
        <>
            <AxleMenu
                isOpen={isMenuOpen}
                close={closeMenu}
                anchorEl={iconButtonRef.current}
                testId='visit-request-action-menu'
                onClick={(event) => {
                    // Prevent all clicks from within these or on the backdrop brought up with this menu
                    // from triggering the table row
                    event.stopPropagation();
                }}
                items={[
                    ...(visitRequest
                        ? [
                              {
                                  condition:
                                      visitRequest.permissions.edit &&
                                      visitRequest.state === VisitRequestState.PENDING,
                                  label: 'Book',
                                  disabled: mutationLoading,
                                  onClick: () => {
                                      closeMenu();
                                      history.push(buildBookVisitRequestLink(visitRequest));
                                  },
                              },
                              {
                                  condition: canReassignVisitRequest,
                                  label: 'Reassign',
                                  onClick: () => {
                                      closeMenu();
                                      onReassignVisitRequest();
                                  },
                                  disabled: !hasServices,
                                  tooltip: !hasServices
                                      ? REASSIGN_DISABLED_NO_SERVICES_TOOLTIP
                                      : undefined,
                              },
                              {
                                  condition: isEditable,
                                  label: 'Edit',
                                  disabled: mutationLoading,
                                  onClick: onEditVisitRequest,
                              },
                              {
                                  condition:
                                      visitRequest.permissions.edit &&
                                      visitRequest.state === VisitRequestState.PENDING,
                                  label: 'Cancel',
                                  disabled: mutationLoading,
                                  onClick: () => {
                                      closeMenu();
                                      void cancelVisitRequest();
                                  },
                              },
                              {
                                  condition:
                                      visitRequest.permissions.edit &&
                                      visitRequest.state === VisitRequestState.CANCELED,
                                  label: 'Restore',
                                  disabled: mutationLoading,
                                  onClick: () => {
                                      closeMenu();
                                      void uncancelVisitRequest();
                                  },
                              },
                              {
                                  condition: !!visitRequest.visit?.id,
                                  label: 'View',
                                  disabled: mutationLoading,
                                  onClick: () => {
                                      if (!visitRequest.visit?.id) {
                                          return;
                                      }
                                      closeMenu();
                                      history.push(`/visits/${visitRequest.visit.id}`);
                                  },
                              },
                          ]
                        : []),
                    {
                        condition: !visitRequest?.visit?.id,
                        label: 'View',
                        disabled: mutationLoading,
                        onClick: () => {
                            closeMenu();
                            history.push(`/visit-requests/${visitRequestId}`);
                        },
                    },
                ]}
            />
            <AxleIconButton
                ref={iconButtonRef}
                icon={<MoreVert />}
                tooltip={'More actions'}
                onClick={(event) => {
                    // Prevent from propagating to table row click events.
                    event.stopPropagation();
                    openMenu();
                }}
                data-test-id='visit-request-action-menu-button'
            />
        </>
    );
};
